/**
 * Gated entry point for the TWG Explorer section.
 *
 * Guards rendering behind the `cc-ai-twg-visualization` experiment and
 * wraps the lazy-loaded inner content in a `TransparentErrorBoundary`
 * so that failures in the graph section never break the space overview
 * page.
 */

import React, { Suspense, lazy } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';

import { TwgExplorerSectionLoadingPlaceholder } from './ui-components/TwgExplorerSectionLoadingPlaceholder.tsx';

const TwgExplorerSectionContent = lazy(() =>
	import(
		/* webpackChunkName: "loadable-twg-explorer-section-content" */
		'./ui-components/TwgExplorerSectionContent.tsx'
	).then((mod) => ({ default: mod.TwgExplorerSectionContent })),
);

export interface TwgExplorerSectionProps {
	cloudId: string;
	spaceId: string;
	testId?: string;
}

export function TwgExplorerSection({
	cloudId,
	spaceId,
	testId,
}: TwgExplorerSectionProps): React.JSX.Element | null {
	if (!expVal('cc-ai-twg-visualization', 'isEnabled', false)) {
		return null;
	}

	return (
		<TransparentErrorBoundary attribution="twg-explorer-section">
			<Suspense fallback={<TwgExplorerSectionLoadingPlaceholder />}>
				<TwgExplorerSectionContent cloudId={cloudId} spaceId={spaceId} testId={testId} />
			</Suspense>
		</TransparentErrorBoundary>
	);
}
export declare namespace TwgExplorerSection {
	export const displayName: string;
}

Object.assign(TwgExplorerSection, {
	displayName: 'TwgExplorerSection',
});
